import { useIntl } from "react-intl";
import { connect } from "react-redux";
import Cookies from "js-cookie";
import { useMountEffect } from "primereact/hooks";
import { Field } from "react-final-form";
import { sessionRequest, resetState, mfaCleared } from "../../../store/slices/authSlice";
import Link from "../../common/link";
import { TextInput, Checkbox } from "../../common/fields";
import Form from "../../common/form";
import * as Validations from "../../common/form/validator";
import { RouteConstants } from "../../../constants";
import PreLoginContainer from "../../common/preLoginContainer";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import {
  CompanySignInButton,
  CompanySignInRedirecting,
  useCompanySignInRedirect,
} from "../sso/CompanySignIn";
import * as GlobalHelper from "../../../helpers/GlobalHelper";
import { errorText } from "../../../helpers/ErrorMessage";

export const LoginLocal = ({
  sessionRequest,
  requesting,
  action,
  error,
  resetState,
  mfaCleared,
  mfa,
  global,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRootDomain = GlobalHelper.CheckIfRootDomain(
    window.location.hostname.split(".")[0]
  );

  // Set once this page sends a password, so only an MFA challenge for that
  // password opens the authenticator step. MFA state left over from earlier
  // (browser Back from the step) must not send the person straight back.
  const submitted = useRef(false);

  useMountEffect(() => {
    resetState();
    // A half-signed-in token from an abandoned MFA step is not a sign-in.
    mfaCleared();
  });

  const passwordOff = useCompanySignInRedirect(global?.enterprise);

  // A correct password while MFA is required: the authenticator step.
  useEffect(() => {
    if (submitted.current && mfa && action === "sign-in" && !requesting) {
      navigate(RouteConstants.APP_ROUTE_LOGIN_MFA);
    }
  }, [mfa, action, requesting, navigate]);

  const userName =
    (searchParams.get("params") &&
      atob(decodeURIComponent(searchParams.get("params")))) ||
    null;

  if (passwordOff) {
    return <CompanySignInRedirecting enterprise={global?.enterprise} />;
  }

  return (
    <PreLoginContainer showLoginMessage={true}>
      <div className="title text-center">
        {intl.formatMessage({
          id: "sign_in_password",
        })}
      </div>
      <CompanySignInButton enterprise={global?.enterprise} />
      <Form
        initialValues={{
          username:
            userName ||
            location?.state?.username ||
            Cookies.get("username") ||
            "",
        }}
        fullWidthButtons
        requesting={action === "sign-in" && requesting}
        submitBtn={{
          label: intl.formatMessage({
            id: "sign_in",
          }),
          icon: "pi-arrow-right",
          onClick: (values) => {
            submitted.current = true;
            sessionRequest(values);
            if (values.rememberMe) {
              Cookies.set("username", values.username, {
                expires: 30,
                secure: true,
                sameSite: "Strict",
              });
            }
          },
        }}
      >
        <div className="flex flex-column gap-2 mt-4">
          <Field
            name="username"
            component={TextInput}
            type="text"
            icon="pi-user"
            placeholder={intl.formatMessage({
              id: "username",
            })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
          <Field
            name="password"
            component={TextInput}
            type="password"
            placeholder={intl.formatMessage({
              id: "password",
            })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
          <Field
            name="rememberMe"
            component={Checkbox}
            type="checkbox"
            label={intl.formatMessage({ id: "remember_me" })}
          />
          {(error && !requesting && action === "sign-in" && (
            <div className="error-message">
              {errorText(error, intl)}
            </div>
          )) ||
            null}
          <div className="flex justify-content-between align-items-center">
            <Link to={RouteConstants.APP_ROUTE_FORGOT_PASSWORD}>
              {intl.formatMessage({
                id: "forgot_password_link",
              })}
            </Link>
            {(global?.enterprise && (
              <Link
                to={RouteConstants.APP_ROUTE_OTP}
                style={{ paddingRight: "0" }}
                state={{
                  username:
                    userName ||
                    location?.state?.username ||
                    Cookies.get("username"),
                }}
              >
                {intl.formatMessage({
                  id: "otp_link",
                })}
              </Link>
            )) ||
              null}
          </div>
        </div>
      </Form>
      {(!isRootDomain && (
        <div className="text-center mt-1">
          <Link to={RouteConstants.APP_ROUTE_LOGIN}>
            {intl.formatMessage({ id: "back_to_login" })}
          </Link>
        </div>
      )) ||
        null}
    </PreLoginContainer>
  );
};

const mapStateToProps = (state) => ({
  requesting: state.auth.requesting,
  error: state.auth.error,
  action: state.auth.action,
  global: state.global,
  mfa: state.auth.mfa,
});

const mapDispatchToProps = (dispatch) => ({
  sessionRequest: (data) => dispatch(sessionRequest(data)),
  resetState: () => dispatch(resetState()),
  mfaCleared: () => dispatch(mfaCleared()),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginLocal);
